import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import { ModalConfig } from './modal.interface';
import { CommonModule } from '@angular/common';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { InlineSVGModule } from 'ng-inline-svg-2';

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule, InlineSVGModule],
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
})
/* It's a component that can be used to display a modal */
export class ModalComponent implements OnInit {
  @ViewChild('modal') private modalContent: TemplateRef<ModalComponent>;
  private modalRef: NgbModalRef;

  @Input() public modalConfig: ModalConfig;
  @Input() public modalOptions: NgbModalOptions;
  @Input() modalClass: string;
  @Input() modalTitle: string;

  // @Input() callbackSubmit: () => void;
  @Input() callbackSubmit?: () => Promise<void> | void;
  @Input() callbackCancel?: () => Promise<void> | void;
  @Input() callbackDetail?: () => Promise<void> | void;

  @Output() modalOpened = new EventEmitter<void>();
  @Output() modalClosed = new EventEmitter<void>();
  @Output() modalDismissed = new EventEmitter<void>();

  defaultOptions: NgbModalOptions = {
    centered: true,
    backdrop: 'static',
    keyboard: false,
  };

  defaultConfig: ModalConfig = {
    showHeader: true,
    showFooter: true,
  };

  constructor(private modalService: NgbModal) {}

  ngOnInit(): void {
    this.modalConfig = {
      ...this.modalConfig,
      showHeader: this.modalConfig.showHeader ?? this.defaultConfig.showHeader,
      showFooter: this.modalConfig.showFooter ?? this.defaultConfig.showFooter,
    };

    this.modalOptions = {
      ...this.modalOptions,
      centered: this.modalOptions?.centered ?? this.defaultOptions.centered,
      backdrop: this.modalOptions?.backdrop ?? this.defaultOptions.backdrop,
      keyboard: this.modalOptions?.keyboard ?? this.defaultOptions.keyboard,
    };

    // this.modalOptions = typeof this.modalOptions === 'undefined' ? this.defaultOptions : { ...this.modalOptions, ...this.defaultOptions };
  }

  open(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.modalRef = this.modalService.open(this.modalContent, this.modalOptions);

      // Call onOpened hook and emit event after modal is opened
      this.modalRef.shown.subscribe(() => {
        if (this.modalConfig.onOpened) {
          this.modalConfig.onOpened();
        }
        this.modalOpened.emit();
      });

      this.modalRef.result.then(resolve, resolve);
    });
  }

  async close(): Promise<void> {
    if (this.modalConfig.shouldClose === undefined || (await this.modalConfig.shouldClose())) {
      const result = this.modalConfig.onClose === undefined || (await this.modalConfig.onClose());
      this.modalRef.close(result);
      this.modalClosed.emit();
    }
  }

  async dismiss(): Promise<void> {
    if (this.modalConfig.shouldDismiss === undefined || (await this.modalConfig.shouldDismiss())) {
      const result = this.modalConfig.onDismiss === undefined || (await this.modalConfig.onDismiss());
      this.modalRef.dismiss(result);
      this.modalDismissed.emit();
    }
  }

  // `dismiss()` sendiri udah manggil `this.modalRef.dismiss()` di dalam - TAPI cuma kalau
  // `shouldDismiss()` (kalau ada) resolve true. Sebelumnya di sini ada panggilan
  // `this.modalRef.dismiss(result)` KEDUA tanpa syarat setelah delegasi ke `dismiss()` - itu
  // nutup modal WALAU shouldDismiss() gagal (harusnya tetap kebuka buat retry), bikin modal
  // ke-dismiss diam-diam tanpa onDismiss() pernah kepanggil. Sekarang cuma delegasi + return.
  async handleSubmit(): Promise<void> {
    if (this.callbackSubmit === undefined) {
      await this.dismiss();
      return;
    }
    this.modalRef.dismiss(await this.callbackSubmit());
  }

  async handleDetail(): Promise<void> {
    if (this.callbackDetail === undefined) {
      await this.dismiss();
      return;
    }
    this.modalRef.dismiss(await this.callbackDetail());
  }

  protected readonly STRING_CONSTANTS = STRING_CONSTANTS;
}
